import {
    Table,
    TableSectionsData
} from "@/libraries/design/patterns/table/src/table";

import { useJpdPlanTitlesAllowList } from "../../pricing-calculator/src/configs/jpd.pricing-calculator-config.ts";

import { transformContentfulDataToTableData } from "./utils";

export interface RelevantPlan {
    fields?: {
        planTitleExternal?: string;
        planTitleInternal?: string;
    };
}

export interface FeatureAdditionalInformation {
    fields?: {
        featureAdditionalInformation?: string;
        relevantPlan: Array<RelevantPlan>;
    };
}

export interface FeatureDataContent {
    nodeType:
        | "text"
        | "hyperlink"
        | "paragraph"
        | "unordered-list"
        | "list-item";
    value?: string;
    content?: FeatureDataContent[];
    data?: {
        uri?: string;
    };
}

export interface FeatureData {
    fields: {
        description?: string;
        featureNameExternal?: string;
        featureNameInternal?: string;
        relevantUrl?: string;
        featureAdditionalInformation?: FeatureAdditionalInformation[];
        relevantPlan?: RelevantPlan[];
        callOut?: string;
    };
}

// expected contentful data structure
export interface ContentfulFeature {
    fields: {
        featureGroupTitleExternal: string;
        featureGroupTitleInternal?: string;
        features?: FeatureData[];
    };
}

export interface UniformPricingTableProps {
    tableData: ContentfulFeature[];
    planTitles: {
        fields: {
            planTitleExternal: string;
        };
    }[];
    /**
     * Optional URL slug used to drive product-specific column filtering.
     * When the urlSlug matches a product whose visible plan set is gated
     * behind a feature flag (currently only `"product-discovery"`, see
     * GWP-10907), columns whose `planTitleExternal` is not in the active
     * allow-list are dropped — so CMS authors can stage new plan columns
     * without them appearing in production until the flag is on.
     */
    urlSlug?: string;
}

const useEffectivePlanTitles = (
    planTitles: UniformPricingTableProps["planTitles"],
    urlSlug?: string
) => {
    // Single source of truth for the JPD plan-title allow-list (gated
    // behind the GWP-10907 feature flag). Always invoked to satisfy the
    // Rules of Hooks; the result is only USED for `product-discovery`.
    const jpdPlanTitlesAllowList = useJpdPlanTitlesAllowList();

    const allTitles = planTitles.map(({ fields }) => fields.planTitleExternal);
    if (urlSlug === "product-discovery") {
        const allowSet = new Set(jpdPlanTitlesAllowList);
        return allTitles.filter((title) => allowSet.has(title));
    }
    return allTitles;
};

const UniformPricingTable = (props: UniformPricingTableProps) => {
    const { tableData = [], planTitles, urlSlug } = props;

    const effectivePlanTitles = useEffectivePlanTitles(planTitles, urlSlug);

    const transformedSections = transformContentfulDataToTableData(
        tableData,
        effectivePlanTitles
    );

    return (
        <Table
            header={{
                titles: ["", ...effectivePlanTitles]
            }}
            sections={transformedSections as TableSectionsData}
        />
    );
};

export default UniformPricingTable;
